<script setup lang="ts">
import { computed, ref } from 'vue'
import { usePrevioDS } from '../../../../../../composables/helpers'
import { useFocusManager } from '../composables/useFocusManager'

type Props = {
  id: string
  text?: null | string | number
  readonly?: boolean
}

type Emits = {
  (e: 'next'): void
  (e: 'prev'): void
  (e: 'add'): void
  (e: 'subtract'): void
  (e: 'delete'): void
  (e: 'confirm'): void
  (e: 'focus'): void
  (e: 'input', value: string): void
}

const props = defineProps<Props>()
const emits = defineEmits<Emits>()

const ds = usePrevioDS()
const focusManager = useFocusManager()

const segmentComponentRef = ref<HTMLElement | null>(null)

const isFocused = computed<boolean>(() => {
  return focusManager.isFocused(props.id)
})

const focus = (): void => {
  if (ds.screen().isMobile || props.readonly) {
    return
  }

  focusManager.focus(props.id)
  segmentComponentRef.value?.focus()
  emits('focus')
}

const onClick = focus

const onKeyDown = (e: KeyboardEvent): void => {
  if (props.readonly) {
    return
  }

  e.preventDefault()
  e.stopPropagation()

  if (e.key === 'Enter') {
    emits('confirm')
    return
  }

  if (e.key === 'ArrowUp') {
    emits('add')
    return
  }

  if (e.key === 'ArrowDown') {
    emits('subtract')
    return
  }

  if (e.key === 'ArrowRight') {
    emits('next')
    return
  }

  if (e.key === 'ArrowLeft') {
    emits('prev')
    return
  }

  if (e.key === 'Delete' || e.key === 'Backspace') {
    emits('delete')
    return
  }

  if (e.key >= '0' && e.key <= '9') {
    emits('input', e.key)
    return
  }
}

defineExpose({
  focus,
})
</script>

<template>
  <div
    ref="segmentComponentRef"
    :class="{ 'ds-segment--focused': isFocused, 'ds-segment--readonly': readonly }"
    class="ds-segment"
    :tabindex="readonly ? -1 : 0"
    @click="onClick"
    @keydown="onKeyDown"
  >
    {{ text }}
  </div>
</template>

<style scoped lang="scss">
@import '../../../../../../assets/scss/shared/mixins';
@import '../../../../../../assets/scss/shared/brand.module';

.ds-segment {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-family: inherit;
  color: getColor('grey600');
  background-color: transparent;
  border-radius: 4px;
  transition: all 0.15s ease;
  user-select: none;
  outline: none;

  @include laptopAndLarge {
    cursor: text;
  }
}

.ds-segment:hover:not(.ds-segment--focused):not(.ds-segment--readonly) {
  @include laptopAndLarge {
    background-color: getColor('grey200');
  }
}

.ds-segment--readonly {
  @include laptopAndLarge {
    cursor: default;
  }
}

.ds-segment--focused {
  background-color: getColor('grey300');
  color: getColor('white');
}
</style>
